import React, { useEffect, useRef } from 'react';

interface Props {
  variant?: string;
  className?: string;
}

const VERT_SRC = `
attribute vec2 position;
void main() {
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

const FRAG_SRC = `
precision highp float;

uniform vec2 u_resolution;
uniform float u_time;
uniform vec3 u_color1;
uniform vec3 u_color2;
uniform vec3 u_color3;
uniform vec3 u_color4;

// Simplex noise implementation
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec3 permute(vec3 x) { return mod289(((x*34.0)+1.0)*x); }

float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187,  // (3.0-sqrt(3.0))/6.0
                      0.366025403784439,  // 0.5*(sqrt(3.0)-1.0)
                     -0.577350269189626,  // -1.0 + 2.0 * C.x
                      0.024390243902439); // 1.0 / 41.0
  vec2 i  = floor(v + dot(v, C.yy) );
  vec2 x0 = v -   i + dot(i, C.xx);
  vec2 i1;
  i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod289(i);
  vec3 p = permute( permute( i.y + vec3(0.0, i1.y, 1.0 ))
    + i.x + vec3(0.0, i1.x, 1.0 ));
  vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
  m = m*m;
  m = m*m;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * ( a0*a0 + h*h );
  vec3 g;
  g.x  = a0.x  * x0.x  + h.x  * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution.xy;
  
  // Create slow moving blobs
  float t = u_time * 0.2;
  
  vec2 p1 = vec2(snoise(vec2(t*0.5, t*0.3)), snoise(vec2(t*0.4, t*0.6)));
  vec2 p2 = vec2(snoise(vec2(t*0.6, t*0.2)), snoise(vec2(t*0.3, t*0.5)));
  vec2 p3 = vec2(snoise(vec2(t*0.2, t*0.4)), snoise(vec2(t*0.5, t*0.3)));

  float w1 = smoothstep(0.0, 1.5, 1.2 - length(uv - p1 * 0.5 - 0.5));
  float w2 = smoothstep(0.0, 1.5, 1.0 - length(uv - p2 * 0.5 - 0.5));
  float w3 = smoothstep(0.0, 1.5, 0.8 - length(uv - p3 * 0.5 - 0.5));
  
  // Base background
  vec3 color = mix(u_color1, u_color2, uv.y + snoise(uv*2.0 + t)*0.2);
  
  // Add blobs
  color = mix(color, u_color3, w1 * 0.8);
  color = mix(color, u_color4, w2 * 0.7);
  color = mix(color, u_color1, w3 * 0.6);
  
  // Smooth out
  color = mix(color, u_color1, snoise(uv*1.5 - t)*0.15);

  // Film grain
  float grain = fract(sin(dot(uv.xy * u_resolution.xy + u_time, vec2(12.9898,78.233))) * 43758.5453);
  color -= grain * 0.05;

  gl_FragColor = vec4(color, 1.0);
}
`;

const PALETTES: Record<string, { c1: number[], c2: number[], c3: number[], c4: number[] }> = {
  azure: { 
    c1: [0.05, 0.1, 0.3], // Dark navy
    c2: [0.1, 0.4, 0.8],  // Medium blue
    c3: [0.5, 0.8, 1.0],  // Light bright blue
    c4: [0.8, 0.95, 1.0], // Ice white/blue
  }
};

export default function WebGLMeshGradientBackground({ variant = 'azure', className = '' }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef    = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext("webgl", { alpha: false, antialias: false });
    if (!gl) return;

    const program = createProgram(gl, VERT_SRC, FRAG_SRC);
    if (!program) return;

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      -1,-1,  1,-1, -1, 1,
      -1, 1,  1,-1,  1, 1
    ]), gl.STATIC_DRAW);

    const posLoc = gl.getAttribLocation(program, "position");
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    const uRes   = gl.getUniformLocation(program, "u_resolution");
    const uTime  = gl.getUniformLocation(program, "u_time");
    const uCol1  = gl.getUniformLocation(program, "u_color1");
    const uCol2  = gl.getUniformLocation(program, "u_color2");
    const uCol3  = gl.getUniformLocation(program, "u_color3");
    const uCol4  = gl.getUniformLocation(program, "u_color4");

    const t0 = performance.now();

    const render = (now: number) => {
      // Handle resize
      const rect = canvas.getBoundingClientRect();
      const ww = rect.width  * (window.devicePixelRatio || 1);
      const hh = rect.height * (window.devicePixelRatio || 1);
      if (canvas.width !== ww || canvas.height !== hh) {
        canvas.width  = ww;
        canvas.height = hh;
        gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
      }

      if (gl.isContextLost()) return;
      gl.useProgram(program);
      gl.uniform2f(uRes, gl.drawingBufferWidth, gl.drawingBufferHeight);
      gl.uniform1f(uTime, (now - t0) * 0.001);

      const palette = PALETTES[variant] || PALETTES['azure'];
      gl.uniform3fv(uCol1, palette.c1);
      gl.uniform3fv(uCol2, palette.c2);
      gl.uniform3fv(uCol3, palette.c3);
      gl.uniform3fv(uCol4, palette.c4);

      gl.drawArrays(gl.TRIANGLES, 0, 6);
      rafRef.current = requestAnimationFrame(render);
    };

    rafRef.current = requestAnimationFrame(render);

    return () => {
      const ext = gl.getExtension('WEBGL_lose_context');
      if (ext) ext.loseContext();
      cancelAnimationFrame(rafRef.current);
      gl.deleteBuffer(buf);
      gl.deleteProgram(program);
    };
  }, [variant]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full object-cover pointer-events-none z-0 ${className}`}
    />
  );
}

function createShader(gl: WebGLRenderingContext, type: number, src: string): WebGLShader | null {
  const s = gl.createShader(type);
  if (!s) return null;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    console.error("Shader compile error:", gl.getShaderInfoLog(s));
    gl.deleteShader(s);
    return null;
  }
  return s;
}

function createProgram(gl: WebGLRenderingContext, vert: string, frag: string): WebGLProgram | null {
  const vs = createShader(gl, gl.VERTEX_SHADER, vert);
  const fs = createShader(gl, gl.FRAGMENT_SHADER, frag);
  if (!vs || !fs) return null;
  const prog = gl.createProgram();
  if (!prog) return null;
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    console.error("Program link error:", gl.getProgramInfoLog(prog));
    return null;
  }
  return prog;
}
